import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { fetchRegMReviews, reviewRegMPreClear } from "../../../services/Axios";
import { errorMessage, formatWindow } from "./regmFormat";
import {
  REVIEW_NOTE_MAX,
  formatAttestedAt,
  reviewStatusLabel,
  validateReviewNote,
} from "./historyFormat";

const COPY = {
  clear: { title: "Clear record", decision: "cleared", submit: "Clear record" },
  reopen: { title: "Reopen record", decision: "reopened", submit: "Reopen record" },
};

// action: "clear" | "reopen". onDone runs after a stored review, the parent reloads the list.
const ReviewDialog = ({ open, record, action, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState(null);
  const submittingRef = useRef(false);
  const recordId = record?.id;

  useEffect(() => {
    if (!open) return undefined;
    setNote("");
    setError(null);
    setReviews(null);
    let cancelled = false;
    // Quiet on failure: the history is informational only.
    fetchRegMReviews(recordId)
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setReviews(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, recordId]);

  const copy = COPY[action];
  if (!record || !copy) return null;
  const check = validateReviewNote(note);

  const submit = async () => {
    if (submittingRef.current || !check.valid) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await reviewRegMPreClear(record.id, { decision: copy.decision, note: note.trim() });
      onDone(record.id);
    } catch (e) {
      setError(e?.response ? errorMessage(e) : e?.message || errorMessage(e));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack spacing={0.25}>
            <Typography variant="body2">
              <strong>{record.security}</strong> {record.isin ? `(${record.isin})` : ""}
            </Typography>
            <Typography variant="body2">Trader: {record.user}</Typography>
            <Typography variant="body2">Attested: {formatAttestedAt(record.attested_at)}</Typography>
            <Typography variant="body2">Pricing date: {record.pricing_date}</Typography>
            <Typography variant="body2">
              Window: {formatWindow(record.window_start, record.window_end)}
            </Typography>
            <Typography variant="body2">Status: {reviewStatusLabel(record.review_status)}</Typography>
          </Stack>
          {reviews && reviews.length > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Review history</Typography>
              {reviews.map((rv) => (
                <Typography key={rv.id} variant="caption" display="block" color="text.secondary">
                  {formatAttestedAt(rv.reviewed_at)} {String(rv.decision)} by {String(rv.reviewer)}:{" "}
                  {String(rv.note)}
                </Typography>
              ))}
            </Stack>
          )}
          <TextField
            label="Note (required)"
            multiline
            minRows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            error={note.length > 0 && !check.valid}
            helperText={`${check.length} / ${REVIEW_NOTE_MAX} characters (minimum 10, whitespace at the ends is ignored)${
              note.length > 0 && check.message ? `. ${check.message}` : ""
            }`}
            autoFocus
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!check.valid || submitting}>
          {submitting ? "Saving..." : copy.submit}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewDialog;
